import { getDeviceId } from "./deviceId";
import { getSupabase } from "./supabase";

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = "unauthorized") {
    super(401, message);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends ApiError {
  constructor(message = "forbidden") {
    super(403, message);
    this.name = "ForbiddenError";
  }
}

export class SpamDetectedError extends ApiError {
  constructor(message = "spam detected") {
    super(403, message);
    this.name = "SpamDetectedError";
  }
}

export class NotFoundError extends ApiError {
  constructor(message = "not found") {
    super(404, message);
    this.name = "NotFoundError";
  }
}

export class ValidationError extends ApiError {
  readonly fields: unknown;
  constructor(fields: unknown) {
    super(422, "validation failed");
    this.fields = fields;
    this.name = "ValidationError";
  }
}

export class RateLimitedError extends ApiError {
  readonly retryAfter: number;
  constructor(retryAfter: number) {
    super(429, "rate limited");
    this.retryAfter = retryAfter;
    this.name = "RateLimitedError";
  }
}

export class NetworkError extends ApiError {
  constructor(cause: unknown) {
    super(0, `network error: ${String(cause)}`);
    this.name = "NetworkError";
  }
}

export class ParseError extends ApiError {
  constructor(cause: unknown) {
    super(0, `response parse error: ${String(cause)}`);
    this.name = "ParseError";
  }
}

// 409 from POST /stations carries `{ created: null, candidates: StationOut[] }`
// when dedupe fires; the form needs the candidates body, not just a status.
export class DuplicateCandidatesError extends ApiError {
  readonly body: unknown;
  constructor(body: unknown) {
    super(409, "duplicate candidates");
    this.body = body;
    this.name = "DuplicateCandidatesError";
  }
}

export type BaseApiError =
  | { kind: "rate-limited"; retryAfter: number }
  | { kind: "spam-detected" }
  | { kind: "unauthorized" }
  | { kind: "validation"; fields?: unknown }
  | { kind: "network" }
  | { kind: "server" }
  | { kind: "error"; message: string };

export function mapApiError(err: unknown): BaseApiError {
  if (err instanceof RateLimitedError) return { kind: "rate-limited", retryAfter: err.retryAfter };
  if (err instanceof SpamDetectedError) return { kind: "spam-detected" };
  if (err instanceof UnauthorizedError) return { kind: "unauthorized" };
  if (err instanceof ValidationError) return { kind: "validation", fields: err.fields };
  if (err instanceof NetworkError) return { kind: "network" };
  if (err instanceof ApiError && err.status >= 500) return { kind: "server" };
  return { kind: "error", message: err instanceof Error ? err.message : "" };
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
};

async function authToken(): Promise<string | null> {
  const { data } = await getSupabase().auth.getSession();
  return data.session?.access_token ?? null;
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_URL;
  if (!baseUrl) throw new Error("VITE_API_URL is required");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Device-Id": getDeviceId(),
  };
  if (opts.auth) {
    const token = await authToken();
    if (!token) throw new UnauthorizedError("authentication required");
    headers.Authorization = `Bearer ${token}`;
  }

  let resp: Response;
  try {
    resp = await fetch(`${baseUrl}${path}`, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw new NetworkError(e);
  }

  if (resp.ok) {
    if (resp.status === 204) return undefined as T;
    try {
      return (await resp.json()) as T;
    } catch (e) {
      throw new ParseError(e);
    }
  }

  const detail = await safeReadDetail(resp);

  if (resp.status === 401) throw new UnauthorizedError(detail);
  if (resp.status === 403) {
    if (detail.toLowerCase().includes("spam")) throw new SpamDetectedError(detail);
    throw new ForbiddenError(detail);
  }
  if (resp.status === 404) throw new NotFoundError(detail);
  if (resp.status === 409) throw new DuplicateCandidatesError(await safeReadJson(resp));
  if (resp.status === 422) throw new ValidationError(await safeReadJson(resp));
  if (resp.status === 429) {
    const retryAfter = Number(resp.headers.get("Retry-After") ?? 60);
    throw new RateLimitedError(Number.isFinite(retryAfter) ? retryAfter : 60);
  }
  throw new ApiError(resp.status, detail || resp.statusText);
}

async function safeReadDetail(resp: Response): Promise<string> {
  try {
    const cloned = resp.clone();
    const json = (await cloned.json()) as { detail?: string };
    return json.detail ?? resp.statusText;
  } catch {
    return resp.statusText;
  }
}

async function safeReadJson(resp: Response): Promise<unknown> {
  try {
    return await resp.json();
  } catch (err) {
    console.warn("[api] response body parse failed", {
      status: resp.status,
      url: resp.url,
      error: String(err),
    });
    return null;
  }
}
